import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowRight, Megaphone, HeartHandshake, BookOpen, HandHeart, Radio, Check } from "lucide-react";
import SectionEyebrow from "@/components/SectionEyebrow";
import { DEPARTMENTS, IMAGES } from "@/data/content";

const ICONS = { Megaphone, HeartHandshake, BookOpen, HandHeart, Radio };

const Departments = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [hash]);

  return (
    <div data-testid="departments-page">
      {/* HEADER */}
      <section className="relative min-h-[55vh] flex items-end overflow-hidden">
        <img src={IMAGES.formation} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B3D91]/90 via-[#0B3D91]/60 to-[#0B3D91]/30" />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative mv-container py-24 text-white">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-white/80" />
            <span className="text-xs uppercase tracking-[0.3em] font-bold">Nos piliers</span>
          </div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Nos Départements.
          </h1>
          <p className="mt-6 text-lg text-blue-50/95 max-w-2xl">
            Découvrez les quatre piliers qui soutiennent la mission de MIREVSA.
          </p>
        </div>
      </section>

      {/* QUICK NAV */}
      <section className="bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="mv-container py-4 flex flex-wrap gap-3">
          {DEPARTMENTS.filter((d) => d.slug !== "communication").map((d) => (
            <a
              key={d.slug}
              href={`#${d.slug}`}
              data-testid={`dept-nav-${d.slug}`}
              className="text-sm font-medium text-[#1C1C1C] hover:text-[#0B3D91] px-4 py-2 border border-gray-200 hover:border-[#0B3D91] transition-colors"
            >
              {d.title}
            </a>
          ))}
        </div>
      </section>

      {/* DEPARTMENT SECTIONS */}
      {DEPARTMENTS.filter((d) => d.slug !== "communication").map((d, idx) => {
        const Icon = ICONS[d.icon];
        const reverse = idx % 2 === 1;
        return (
          <section
            key={d.slug}
            id={d.slug}
            className={`py-24 ${idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"} scroll-mt-32`}
            data-testid={`dept-section-${d.slug}`}
          >
            <div className="mv-container">
              <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}>
                <div className={`lg:col-span-5 ${reverse ? "lg:[direction:ltr]" : ""}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={d.image} alt={d.title} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className={`lg:col-span-7 ${reverse ? "lg:[direction:ltr]" : ""}`}>
                  <div className="h-12 w-12 bg-[#0B3D91] flex items-center justify-center mb-6">
                    <Icon size={22} className="text-white" />
                  </div>
                  <SectionEyebrow>Département</SectionEyebrow>
                  <h2 className="mt-3 font-heading font-black text-4xl sm:text-5xl tracking-tight text-[#1C1C1C]">
                    {d.title}
                  </h2>
                  <p className="mt-5 text-lg text-muted leading-relaxed">{d.short}</p>

                  <div className="mt-10 space-y-5">
                    {d.subsections.map((s) => (
                      <div key={s.title} className="flex gap-4">
                        <div className="h-6 w-6 bg-[#0B3D91] flex items-center justify-center shrink-0 mt-1">
                          <Check size={14} className="text-white" />
                        </div>
                        <div>
                          <div className="font-heading font-bold text-lg text-[#1C1C1C]">{s.title}</div>
                          <div className="text-base text-muted">{s.text}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={d.ctaLink}
                    data-testid={`dept-cta-${d.slug}`}
                    className="mt-10 btn-primary inline-flex"
                  >
                    {d.cta} <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default Departments;
